/**
 * HTTP layer for the reports endpoints. Pure axios — no Redux, no UI
 * concerns. The slice in `slices/reportSlice.ts` wraps these in
 * createAsyncThunk so React components only see actions/state.
 *
 * Linear: MYD-196 (C3) original evaluation-only build; generalized to
 * scope-aware in MYD-339 (Phase 2) so Projects / Locations / Portfolios
 * share the same service / slice / UI surface.
 *
 * Scope → base URL mapping:
 *   - evaluation → /api/evaluations/:entityId/reports
 *   - project    → /api/projects/:entityId/reports
 *   - location   → /api/locations/:entityId/reports   (Phase 3)
 *   - portfolio  → /api/portfolios/:entityId/reports  (Phase 4)
 */

import axios from 'axios'

export type ReportScope = 'evaluation' | 'project' | 'location' | 'portfolio'

export interface TemplateDescriptor {
	key: string
	label: string
	description: string
	supportedFormats: Array<'PDF' | 'DOCX' | 'HTML' | 'XLSX'>
	supportedScopes?: ReportScope[]
	// MYD-342 / MYD-343: routing hint that may also affect what inputs the
	// dialog renders. `partner-packet` triggers a recipient + sender-note
	// form section in addition to the template / format pickers.
	aggregatorMode?: 'standard' | 'financial-only' | 'partner-packet'
	// MYD-343: names of caller-supplied args the dialog must collect before
	// Generate becomes enabled.
	requiredTemplateArgs?: string[]
}

export type ReportFormat = 'PDF' | 'DOCX' | 'HTML' | 'XLSX'

export type ReportStatus = 'PENDING' | 'READY' | 'FAILED'

export type ReportSource = 'cloudinary' | 'local' | 'r2'

export interface ReportRow {
	_id: string
	scope?: ReportScope
	// Exactly one of these is populated to match `scope`. For backwards
	// compatibility with the legacy `EvaluationReport` rows, evaluation-scope
	// reports also carry `evaluation` at the top level.
	evaluation?: string
	project?: string
	location?: string
	portfolio?: string
	templateKey?: string
	templateName?: string
	format: ReportFormat
	fileUrl?: string
	status: ReportStatus
	error?: string
	source?: ReportSource
	libraryDocument?: string
	generatedBy: { _id: string; name?: string; email?: string } | string
	/**
	 * Whether the viewer may delete this row: its generator or the asset's
	 * owner (MYD-524). Sent for location, project and portfolio reports; absent
	 * on evaluation reports, whose delete rule is the evaluation owner's.
	 */
	canDelete?: boolean
	generatedAt: string
	createdAt?: string
	updatedAt?: string
}

const SCOPE_TO_PATH: Record<ReportScope, string> = {
	evaluation: 'evaluations',
	project: 'projects',
	location: 'locations',
	portfolio: 'portfolios',
}

function baseUrl(scope: ReportScope, entityId: string): string {
	const segment = SCOPE_TO_PATH[scope]
	if (!segment) throw new Error(`Unsupported scope: ${scope}`)
	return `/api/${segment}/${entityId}/reports`
}

function authHeaders(token: string) {
	return { headers: { Authorization: `Bearer ${token}` } }
}

export async function listReports(
	scope: ReportScope,
	entityId: string,
	token: string,
): Promise<ReportRow[]> {
	const { data } = await axios.get<ReportRow[]>(baseUrl(scope, entityId), authHeaders(token))
	return data
}

export async function listTemplates(
	scope: ReportScope,
	entityId: string,
	token: string,
): Promise<TemplateDescriptor[]> {
	const { data } = await axios.get<TemplateDescriptor[]>(
		`${baseUrl(scope, entityId)}/templates`,
		authHeaders(token),
	)
	return data
}

/**
 * Fire the generator. Synchronous on the server today; resolves once the row
 * has transitioned from PENDING to READY (or rejects when the row is FAILED).
 *
 * `templateArgs` is forwarded verbatim to the aggregator on the server. For
 * partner-packet (MYD-343) callers must include `{ recipientName,
 * recipientCompany?, recipientEmail?, senderNote? }`. Sender name/email are
 * filled in server-side from the authenticated user.
 */
export async function generate(
	scope: ReportScope,
	entityId: string,
	body: {
		templateKey: string
		format: ReportFormat
		templateArgs?: Record<string, unknown>
	},
	token: string,
): Promise<ReportRow> {
	const { data } = await axios.post<ReportRow>(
		`${baseUrl(scope, entityId)}/generate`,
		body,
		authHeaders(token),
	)
	return data
}

export async function deleteReport(
	scope: ReportScope,
	entityId: string,
	reportId: string,
	token: string,
): Promise<void> {
	await axios.delete(`${baseUrl(scope, entityId)}/${reportId}`, authHeaders(token))
}

/**
 * Build the download URL for a report. Resolved by the controller into a 302
 * redirect to either Cloudinary / R2 / the local /static/reports mount.
 */
export function downloadUrl(scope: ReportScope, entityId: string, reportId: string): string {
	return `${baseUrl(scope, entityId)}/${reportId}/download`
}

/**
 * Fetch a generated report as a Blob.
 *
 * MYD-344: the iframe / new-tab navigation path that previously loaded the
 * download URL directly couldn't be proxied by CRA's package.json `"proxy"`
 * (it ignores requests with `text/html` Accept headers). Callers fetch via
 * XHR (which IS proxied), then build a `URL.createObjectURL(blob)` for the
 * iframe / window.open. Works in dev AND prod, no proxy config required.
 */
export async function fetchReportBlob(
	scope: ReportScope,
	entityId: string,
	reportId: string,
	token: string,
): Promise<Blob> {
	const { data } = await axios.get<Blob>(downloadUrl(scope, entityId, reportId), {
		headers: { Authorization: `Bearer ${token}` },
		responseType: 'blob',
	})
	return data
}

/**
 * Build the state-keying string used by the slice's `bySource` map. Stable
 * shape: `${scope}:${entityId}` — keep callers using this helper so the
 * exact format isn't duplicated across components.
 */
export function sourceKey(scope: ReportScope, entityId: string): string {
	return `${scope}:${entityId}`
}
